import React, { useEffect, useState } from 'react'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { CFormLabel } from '@coreui/react'
import { useAuth } from '../contexts/AuthContext'
import { useReport } from '../contexts/ReportContext'
import { useCompanySetting } from '../contexts/CompanySettingContext'
import { colors, convertDateFormat, formatDateToBackend, openFileWithLoading } from '../components/Helper'
import { reportNav } from '../components/NavigationPath'
import Loader from '../components/Loader'
import Layout2 from '../components/Layout2'
import PieChart from '../components/PieChart'
import LineChart from '../components/LineChart'
import PageHeader from '../components/PageHeader'
import DateRangeSelection from '../components/DateRangeSelection'

function MonthReport() {
    const { monthReport, monthReportCharts, monthReportPDF, monthReportCSV, } = useReport();
    const { currency } = useCompanySetting()
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const [dates, setDates] = useState([startOfMonth, endOfMonth]);
    const [reportList, setReportList] = useState([])
    const [sortField, setSortField] = useState('date');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [paymentChartData, setPaymentChartData] = useState([])
    const [monthlyRevenue, setMonthlyRevenue] = useState({
        x: [],
        y: []
    })
    const [monthlyReceipt, setMonthlyReceipt] = useState({
        x: [],
        y: []
    })

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null

    const fetchMonthReport = async () => {
        try {
            setIsLoading(true);
            setReportList([]);
            setTotalRecords(0);
 
            const reports = await monthReport(currentPage, rowsPerPage, sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data);
                setTotalRecords(reports.totalCount);
            }
 
        } catch (error) {
            console.error('Error fetching month reports', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchMonthReport();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId, startDate, endDate]);

    const fetchMonthReportCharts = async () => {
        try {
            setIsLoading(true);

            const reports = await monthReportCharts(startDate, endDate);
            if (reports.paymentChart.length > 0) {
                const paymentData = reports.paymentChart.map((item, index) => ({
                    id: item.id,
                    value: parseFloat(item.value),
                    label: item.label,
                    color: colors[index % colors.length]
                }));

                setPaymentChartData(paymentData);
            } else {
                setPaymentChartData([]);
            }

            if (reports.monthlyRevenue.length > 0) {
                const dateRange = reports.monthlyRevenue.map(item => item.dateRange);
                const revenues = reports.monthlyRevenue.map(item => parseFloat(item.revenue));

                setMonthlyRevenue({
                    x: dateRange,
                    y: revenues
                })
            }

            if (reports.monthlyOrders.length > 0) {
                const dateRange = reports.monthlyOrders.map(item => item.dateRange);
                const orders = reports.monthlyOrders.map(item => parseFloat(item.orders));

                setMonthlyReceipt({
                    x: dateRange,
                    y: orders
                })
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching month report charts', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchMonthReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchMonthReport()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const generatePDF = async (startDate, endDate, pdfType) => {
        openFileWithLoading({
            apiCall: monthReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: 'Generating Summary PDF...',
            errorMessage: 'Failed to generate Summary PDF',
        });
    };

    const generateCSV = async (startDate, endDate, csvType) => {       
        openFileWithLoading({
            apiCall: monthReportCSV,
            apiParams: [startDate, endDate, csvType],
            fileType: 'csv',
            loadingMessage: 'Generating Detailed CSV...',
            errorMessage: 'Failed to generate Detailed CSV',
        });
    }

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Month Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-between'>
                    <p className='pt-2'>
                        Print or download the report for current month: <span className='view_button' onClick={() => generatePDF(startDate, endDate, 'summary')}>Summary PDF</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {
                            monthlyRevenue.x.length > 0 && monthlyRevenue.y.length > 0 && (
                                <div className='w-100 text-center me-5'>
                                    <CFormLabel>Revenue</CFormLabel>
                                    <LineChart
                                        x={monthlyRevenue.x}
                                        y={monthlyRevenue.y}
                                        yLabel={'Revenue'}
                                    />
                                </div>
                            )
                        }
                        {
                            monthlyReceipt.x.length > 0 && monthlyReceipt.y.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Receipt</CFormLabel>
                                    <LineChart
                                        x={monthlyReceipt.x}
                                        y={monthlyReceipt.y}
                                        yLabel={'Receipts'}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center'>
                                    <CFormLabel>Payment Amount</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                    </div>
                    <div className='border-bottom mb-3'>
                        <p className='labour-clock-time'>Monthly Report</p>
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={reportList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No month reports found."
                        >
                            <Column
                                field="startsAt"
                                header="Starts At"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="endsAt"
                                header="Ends At"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="receipts"
                                header="#Receipts"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="customerCount"
                                header="#Customers"
                                className='receipt_report_table'
                                sortable
                            />
                            <Column
                                field="netRevenue"
                                header="Revenue"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {currency}{rowData.revenue}
                                    </div>
                                )}
                            />
                            <Column
                                field="revenue"
                                header="Tax"
                                className='receipt_report_table'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {parseFloat(rowData.taxAmount) === 0 ? `[No revenues]` : `${currency}${rowData.taxAmount}`}
                                    </div>
                                )}
                            />
                            <Column
                                header="Action"
                                align="left"
                                className='receipt_report_table'
                                body={(rowData) => (
                                    <div >
                                        <p><span className='view_button' onClick={() => generatePDF(convertDateFormat(rowData.startsAt), convertDateFormat(rowData.monthEndsAt), 'summary')}>PDF</span> / <span className='view_button' onClick={() => generateCSV(convertDateFormat(rowData.startsAt), convertDateFormat(rowData.monthEndsAt), 'summary')}>CSV</span></p>
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default MonthReport